import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleMillionLast() {
  return (
    <ArticleLayout
      meta={{
        slug: "how-long-will-a-million-last",
        hub: "Retirement Calculators",
        time: "7 min read",
        title: "How Long Will $1 Million Last in Retirement? | Centennial",
        seoDescription:
          "How long $1 million lasts at $40k, $50k, $60k, and $80k per year of spending — with real simulation data, the impact of Social Security, and a free calculator to test your own plan.",
        h1: "How Long Will $1 Million Last in Retirement?",
        hook: "Anywhere from 12 years to forever — the difference is your spending rate. Here's the honest math, from real simulations.",
        quickAnswer:
          "At $40,000/year of inflation-adjusted spending (4%), $1 million lasts 30+ years and usually grows. At $50,000 (5%) it survives to age 100 in about 91% of market scenarios. At $60,000 (6%) that drops to roughly 75%, and at $80,000 (8%) the money is typically gone in 14–18 years. Add Social Security and every one of those numbers improves dramatically — most couples only need $20k–$35k/year from the portfolio itself.",
      }}
    >
      <H2>The spending rate is everything</H2>
      <p>
        Using the same 105,000-simulation dataset from our{" "}
        <Link className="text-[#2B5339] underline" to="/learn/planning-to-100-study">age-90-to-100 study</Link>{" "}
        (retire at 65, 60/35/5 portfolio, inflation-adjusted withdrawals, planning to 100):
      </p>
      <DataTable
        head={["Annual spending", "Rate", "Survives to age 100", "Typical outcome"]}
        rows={[
          ["$30,000", "3.0%", "99.9%", "Grows to ~$4.7M median"],
          ["$40,000", "4.0%", "98.1%", "Grows to ~$3.6M median"],
          ["$50,000", "5.0%", "90.8%", "Grows in most runs; fails in ~1 of 11"],
          ["$60,000", "6.0%", "74.7%", "Fails in ~1 of 4 runs"],
          ["$80,000", "8.0%", "Low", "Typically exhausted in 14–18 years"],
        ]}
      />
      <p>
        Notice there is no single answer to "how long will it last" — markets deal different
        hands. That's why a range ("91% of scenarios survive") is honest and a single year
        ("your money runs out at 87") is not.
      </p>

      <H2>Social Security changes the question</H2>
      <p>
        A couple collecting $45,000/year in Social Security who spends $75,000 only needs
        $30,000 from the portfolio — a 3% rate that survives essentially every scenario. The
        right question isn't "how long will $1M last?" but "how big is my spending{" "}
        <em>gap</em>?" (Worked examples: <Link className="text-[#2B5339] underline" to="/learn/how-much-to-retire-at-65">how much do I need to retire at 65?</Link>)
      </p>

      <H2>The first decade decides everything</H2>
      <p>
        Two retirees with identical spending can get opposite outcomes depending on whether bad
        markets arrive early or late —{" "}
        <Link className="text-[#2B5339] underline" to="/learn/sequence-risk">sequence-of-returns risk</Link>.
        Defenses that measurably help: 1–2 years of cash, flexible guardrail spending (skip the
        inflation raise after a down year), and part-time income in the early years.
      </p>

      <H2>Test your own number in 60 seconds</H2>
      <p>
        The free <Link className="text-[#2B5339] underline" to="/">Centennial calculator</Link>{" "}
        runs your exact savings, spending, and Social Security through 5,000 simulations to age
        100 — plus the 1929, 1973, 2000, and 2008 sequences. No signup. Then{" "}
        <Link className="text-[#2B5339] underline" to="/compare">compare two spending levels side by side</Link>{" "}
        to see what an extra $10k/year really costs.
      </p>
    </ArticleLayout>
  );
}
